/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { DeviceLocation, ExtendedDeviceAttributes } from '../constants/telosair-interfaces';
import { generateDeviceCoordinates } from '../_helpers/globalHelper';
import Dropdown from '../controls/DropDown';

type LocationCaptureProps = {
  imageSrc?: string | null;
  selectedDevices: ExtendedDeviceAttributes[];
  newDevicesWithLocation: DeviceLocation;
  setNewDevicesWithLocation: (data: DeviceLocation) => void;
};

/**
 * Used to capture sensor locations for a given floor map.
 * @param props - Props.
 * @returns Image with a functionality to map devices.
 */
export default function LocationCapture(props: LocationCaptureProps): ReactElement {
  const { imageSrc, selectedDevices, newDevicesWithLocation, setNewDevicesWithLocation } = props;
  const [coordinates, setCoordinates] = useState({ x: 0, y: 0 });
  const [isOpen, setIsOpen] = useState(false);
  const [rows, setRows] = useState<string>('2');
  const [currentDevices, setCurrentDevices] = useState<ExtendedDeviceAttributes[]>([]);
  const taggedDeviceCount = Object.keys(newDevicesWithLocation).length;

  useEffect(() => {
    const devicesToBeTagged = selectedDevices.map((selectedDevice: ExtendedDeviceAttributes) => {
      return { ...selectedDevice, tagged: false };
    });
    setCurrentDevices(devicesToBeTagged);
  }, [selectedDevices]);

  const ref = useRef<HTMLImageElement>(null);
  const noMapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  /**
   * Handle a click on image.
   * @param e - The click event.
   */
  const handleImageClick = (e: React.MouseEvent<HTMLImageElement>): void => {
    const obj = { x: e.nativeEvent.offsetX - 12, y: e.nativeEvent.offsetY - 12 };
    setCoordinates(obj);
    setIsOpen(true);
  };

  /**
   * Sets width and height of the image on load.
   */
  const setWidthAndHeightOfImage = (): void => {
    const widthTemp = (ref.current as any).offsetWidth;
    const heightTemp = (ref.current as any).offsetHeight;
    setWidth(widthTemp);
    setHeight(heightTemp);
  };
  const hasFloorMap = imageSrc !== 'default' && !!imageSrc;
  /**
   * Handle options select.
   * @param e - The input select.
   */
  const handleOptionSelect = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const selectedDevice: ExtendedDeviceAttributes = JSON.parse(e.target.value);
    const newDeviceWithLocationData = {
      ...newDevicesWithLocation,
      [selectedDevice.id]: {
        x: coordinates.x / width,
        y: coordinates.y / height
      }
    };
    setNewDevicesWithLocation(newDeviceWithLocationData);
    setIsOpen(false);
  };

  useEffect(() => {
    if (noMapRef.current && !hasFloorMap) {
      setWidth(noMapRef.current.offsetWidth);
      setHeight(noMapRef.current.offsetHeight);
    }
  }, [imageSrc]);

  useEffect(() => {
    if (!hasFloorMap && noMapRef.current) {
      const widthToUse = noMapRef.current.offsetWidth;
      const heightToUse = noMapRef.current.offsetHeight;
      const newCoords = generateDeviceCoordinates(
        currentDevices,
        Number(rows),
        widthToUse,
        heightToUse
      );
      setNewDevicesWithLocation(newCoords);
    } else {
      setNewDevicesWithLocation({});
    }
  }, [imageSrc, rows, currentDevices]);

  return (
    <>
      <div className="justify-start items-center gap-1 inline-flex text-gray-500 text-sm font-normal leading-none">
        Total devices : {selectedDevices.length} | Tagged devices : {taggedDeviceCount}
      </div>
      <div className="w-44">
        <Dropdown
          handleOptionsSelect={(e) => {
            setRows(JSON.parse(e.target.value).val);
          }}
          field="val"
          label="Rows count"
          name="rows"
          options={[1, 2, 3, 4].map((val) => ({ val }))}
        />
      </div>
      <div className="relative my-5 rounded-lg border border-gray-700 dark:border-gray-700 ">
        {/* eslint-disable-next-line */}
        {hasFloorMap ? (
          <img
            ref={ref}
            onLoad={setWidthAndHeightOfImage}
            src={imageSrc || ''}
            alt="Preview"
            className="w-full h-full rounded-lg"
            onClick={handleImageClick}
          />
        ) : (
          <div
            ref={noMapRef}
            onClick={handleImageClick}
            role="banner"
            className="w-full h-56 rounded-lg"
          />
        )}
        {Object.entries(newDevicesWithLocation).map(([key, item]) => {
          return (
            <div
              key={key}
              style={{
                position: 'absolute',
                top: item.y * height,
                left: item.x * width
              }}
            >
              <div className="h-6 w-6 rounded-full bg-primary-500" />
            </div>
          );
        })}
        {isOpen && (
          <div
            className="text-gray-500 dark:text-gray-200 text-xs"
            style={{
              position: 'absolute',
              top: coordinates.y,
              left: coordinates.x
            }}
          >
            <select onChange={handleOptionSelect} className="p-1 dark:bg-container-300">
              <option value="">Select device</option>
              {currentDevices
                .filter((item) => item.active)
                .map((currentDevice: ExtendedDeviceAttributes) => {
                  return (
                    <option
                      className="font-normal text-gray-500 dark:text-gray-200"
                      key={currentDevice.id}
                      value={JSON.stringify(currentDevice)}
                    >
                      {currentDevice.serialNumber}
                      {newDevicesWithLocation[currentDevice.id] ? '- tagged' : ''}
                    </option>
                  );
                })}
            </select>
          </div>
        )}
      </div>
      {/* <div className="text-gray-500 dark:text-gray-300">
        Width: {width}; height: {height}
        <h2>Locations:</h2>
        <ul>
          {Object.entries(newDevicesWithLocation).map(([key, item]) => (
            <li key={key}>{`${key} - (${item.x}, ${item.y})`}</li>
          ))}
        </ul>
      </div> */}
    </>
  );
}
